import { useCallback, useEffect, useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { RRule, Frequency, Weekday, type Options } from "rrule";
import { FormField } from "./FormField";
import { AdminCalendar } from "./AdminCalendar";

type WeekdayWithNth = { weekday: number; n: number };
type WeekdayLike = { weekday: number };

const isWeekdayLike = (v: unknown): v is WeekdayLike =>
  typeof v === "object" &&
  v !== null &&
  "weekday" in v &&
  typeof (v as { weekday: unknown }).weekday === "number";

const isWeekdayWithNth = (v: unknown): v is WeekdayWithNth =>
  typeof v === "object" &&
  v !== null &&
  "weekday" in v &&
  "n" in v &&
  typeof (v as { weekday: unknown }).weekday === "number" &&
  typeof (v as { n: unknown }).n === "number";

interface RecurrencePickerProps {
  id: string;
  label: string;
  value: string;
  onChange: (rruleString: string) => void;
  error?: string;
  helperText?: string;
}

const freqOptions = [
  { label: "Daily", value: Frequency.DAILY },
  { label: "Weekly", value: Frequency.WEEKLY },
  { label: "Monthly", value: Frequency.MONTHLY },
];

const dayOptions = [
  { label: "M", value: 0 },
  { label: "T", value: 1 },
  { label: "W", value: 2 },
  { label: "T", value: 3 },
  { label: "F", value: 4 },
  { label: "S", value: 5 },
  { label: "S", value: 6 },
];

const weekOfMonthOptions = [
  { label: "First", value: 1 },
  { label: "Second", value: 2 },
  { label: "Third", value: 3 },
  { label: "Fourth", value: 4 },
  { label: "3rd to last", value: -3 },
  { label: "2nd to last", value: -2 },
  { label: "Last", value: -1 },
];

const dayOfWeekOptions = [
  { label: "Monday", value: 0 },
  { label: "Tuesday", value: 1 },
  { label: "Wednesday", value: 2 },
  { label: "Thursday", value: 3 },
  { label: "Friday", value: 4 },
  { label: "Saturday", value: 5 },
  { label: "Sunday", value: 6 },
];

function isFrequency(value: unknown): value is Frequency {
  if (typeof value !== "number") return false;
  const allowed = new Set<number>([
    Frequency.DAILY,
    Frequency.WEEKLY,
    Frequency.MONTHLY,
  ]);
  return allowed.has(value);
}

function isWeekIndex(value: unknown): value is number {
  return (
    typeof value === "number" &&
    weekOfMonthOptions.some((opt) => opt.value === value)
  );
}

function isDayOfWeek(value: unknown): value is number {
  return (
    typeof value === "number" &&
    dayOfWeekOptions.some((opt) => opt.value === value)
  );
}

export function RecurrencePicker({
  id,
  label,
  value,
  onChange,
  error,
  helperText,
}: RecurrencePickerProps) {
  const maxCount = 30;

  type RecurrenceUiState = {
    freq: Frequency;
    interval: number;
    selectedDays: number[];
    monthlyType: "dayOfMonth" | "dayOfWeek";
    weekIndex: number;
    dayOfWeek: number;
    endType: "count" | "until";
    count: number;
    until: Date | null;
  };

  const parsed = useMemo<RecurrenceUiState>(() => {
    if (!value || value.trim() === "") {
      return {
        freq: Frequency.WEEKLY,
        interval: 1,
        selectedDays: [],
        monthlyType: "dayOfMonth",
        weekIndex: 1,
        dayOfWeek: 0,
        endType: "count",
        count: 5,
        until: null,
      };
    }

    try {
      const rule = RRule.fromString(value);
      const freq = rule.options.freq;
      const interval = rule.options.interval;

      let selectedDays: number[] = [];
      if (freq === Frequency.WEEKLY && rule.options.byweekday) {
        const raw = rule.options.byweekday as unknown;
        selectedDays = (Array.isArray(raw) ? raw : [])
          .map((d) => {
            if (typeof d === "number") return d;
            if (isWeekdayLike(d)) return d.weekday;
            return null;
          })
          .filter((d): d is number => typeof d === "number");
      }

      let monthlyType: "dayOfMonth" | "dayOfWeek" = "dayOfMonth";
      let weekIndex = 1;
      let dayOfWeek = 0;
      if (freq === Frequency.MONTHLY) {
        const bynweekday = rule.options.bynweekday;
        if (Array.isArray(bynweekday) && bynweekday.length > 0) {
          const [weekday, nth] = bynweekday[0];
          if (typeof weekday === "number" && typeof nth === "number") {
            monthlyType = "dayOfWeek";
            dayOfWeek = weekday;
            weekIndex = nth;
          }
        } else if (rule.options.byweekday) {
          const first = Array.isArray(rule.options.byweekday)
            ? (rule.options.byweekday as unknown[])[0]
            : undefined;
          if (isWeekdayWithNth(first)) {
            monthlyType = "dayOfWeek";
            weekIndex = first.n;
            dayOfWeek = first.weekday;
          }
        }
      }

      let endType: "count" | "until" = "count";
      let count = 5;
      let until: Date | null = null;
      if (rule.options.count) {
        endType = "count";
        count = Math.min(rule.options.count, maxCount);
      } else if (rule.options.until) {
        endType = "until";
        until = rule.options.until;
      }

      return {
        freq,
        interval,
        selectedDays,
        monthlyType,
        weekIndex,
        dayOfWeek,
        endType,
        count,
        until,
      };
    } catch {
      // Invalid RRULE string - fall back to default state
      return {
        freq: Frequency.WEEKLY,
        interval: 1,
        selectedDays: [],
        monthlyType: "dayOfMonth",
        weekIndex: 1,
        dayOfWeek: 0,
        endType: "count",
        count: 5,
        until: null,
      };
    }
  }, [maxCount, value]);

  const toRRuleString = useCallback((next: RecurrenceUiState): string => {
    const options: Partial<Options> = {
      freq: next.freq,
      interval: next.interval,
    };

    if (next.freq === Frequency.WEEKLY && next.selectedDays.length > 0) {
      options.byweekday = next.selectedDays;
    }

    if (next.freq === Frequency.MONTHLY && next.monthlyType === "dayOfWeek") {
      const weekday = new Weekday(next.dayOfWeek);
      options.byweekday = [weekday.nth(next.weekIndex)];
    }

    if (next.endType === "count") {
      options.count = Math.min(Math.max(1, next.count), maxCount);
    } else if (next.endType === "until" && next.until) {
      options.until = next.until;
    }

    const rule = new RRule(options);
    const rruleString = rule.toString();
    return rruleString.split("\n").pop() || rruleString;
  }, [maxCount]);

  const emit = useCallback((next: RecurrenceUiState) => {
    const nextValue = toRRuleString(next);
    if (nextValue !== value) onChange(nextValue);
  }, [onChange, toRRuleString, value]);

  // Ensure an initial RRULE is set.
  useEffect(() => {
    if (value && value.trim() !== "") return;
    emit(parsed);
  }, [emit, parsed, value]);

  const freq = parsed.freq;
  const interval = parsed.interval;
  const selectedDays = parsed.selectedDays;
  const monthlyType = parsed.monthlyType;
  const weekIndex = parsed.weekIndex;
  const dayOfWeek = parsed.dayOfWeek;
  const endType = parsed.endType;
  const count = parsed.count;
  const until = parsed.until;

  return (
    <FormField id={id} label={label} error={error} helperText={helperText} required>
      <div className="flex flex-column gap-4 p-4 border-1 border-200 border-round shadow-sm bg-white">
        {/* Info box explaining that the first event uses the start date */}
        <div className="flex align-items-start gap-2 p-3 bg-blue-50 border-left-3 border-blue-500 border-round">
          <i className="pi pi-info-circle text-blue-600 mt-1" style={{ fontSize: "1.1rem" }}></i>
          <div className="flex-1">
            <p className="text-sm text-900 m-0 line-height-3">
              <strong>Note:</strong> The first event in the recurring series will use the event start date and time you set in the Basic Information section.
            </p>
          </div>
        </div>
        
        {/* Frequency & Interval */}
        <div className="grid align-items-center">
          <div className="col-12 md:col-4">
            <span className="block mb-2 font-semibold text-900">Repeat frequency</span>
            <Dropdown
              value={freq}
              options={freqOptions}
              onChange={(e) => {
                if (isFrequency(e.value)) {
                  emit({ ...parsed, freq: e.value });
                }
              }}
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-4">
            <span className="block mb-2 font-semibold text-900">Every</span>
            <div className="flex align-items-center gap-2">
              <InputNumber
                value={interval}
                onValueChange={(e) =>
                  emit({ ...parsed, interval: e.value || 1 })
                }
                min={1}
                max={99}
                className="w-full"
                inputClassName="text-center"
              />
              <span className="text-600 white-space-nowrap">
                {freq === Frequency.DAILY ? "day(s)" : freq === Frequency.WEEKLY ? "week(s)" : "month(s)"}
              </span>
            </div>
          </div>
        </div>

        {/* Weekly Day Selector */}
        {freq === Frequency.WEEKLY && (
          <div className="flex flex-column gap-2 animate-fadein">
            <span className="block font-semibold text-900">On these days</span>
            <SelectButton
              value={selectedDays}
              options={dayOptions}
              onChange={(e) => {
                const next = Array.isArray(e.value)
                  ? e.value.filter((d): d is number => typeof d === "number")
                  : [];
                emit({ ...parsed, selectedDays: next });
              }}
              multiple
              className="flex-wrap"
            />
          </div>
        )}

        {/* Monthly Specifics */}
        {freq === Frequency.MONTHLY && (
          <div className="flex flex-column gap-3 animate-fadein border-top-1 border-100 pt-3">
            <div className="flex align-items-center gap-2 cursor-pointer">
              <RadioButton
                inputId="monthly-day"
                name="monthlyType"
                value="dayOfMonth"
                onChange={() =>
                  emit({ ...parsed, monthlyType: "dayOfMonth" })
                }
                checked={monthlyType === "dayOfMonth"}
              />
              <label htmlFor="monthly-day" className="text-900 cursor-pointer">On the same day of the month</label>
            </div>
            <div className="flex align-items-center gap-2">
              <RadioButton
                inputId="monthly-weekday"
                name="monthlyType"
                value="dayOfWeek"
                onChange={() =>
                  emit({ ...parsed, monthlyType: "dayOfWeek" })
                }
                checked={monthlyType === "dayOfWeek"}
              />
              <label htmlFor="monthly-weekday" className="text-900 cursor-pointer">
                On the
              </label>
              <Dropdown
                value={weekIndex}
                options={weekOfMonthOptions}
                onChange={(e) => {
                  if (isWeekIndex(e.value)) {
                    emit({
                      ...parsed,
                      weekIndex: e.value,
                      monthlyType: "dayOfWeek",
                    });
                  }
                }}
                onFocus={() => {
                  if (monthlyType !== "dayOfWeek") {
                    emit({ ...parsed, monthlyType: "dayOfWeek" });
                  }
                }}
                className="w-8rem"
              />
              <Dropdown
                value={dayOfWeek}
                options={dayOfWeekOptions}
                onChange={(e) => {
                  if (isDayOfWeek(e.value)) {
                    emit({
                      ...parsed,
                      dayOfWeek: e.value,
                      monthlyType: "dayOfWeek",
                    });
                  }
                }}
                onFocus={() => {
                  if (monthlyType !== "dayOfWeek") {
                    emit({ ...parsed, monthlyType: "dayOfWeek" });
                  }
                }}
                className="w-10rem"
              />
            </div>
          </div>
        )}

        {/* End Condition */}
        <div className="border-top-1 border-100 pt-3">
          <span className="block mb-3 font-semibold text-900">End conditions</span>
          <div className="flex flex-column gap-3">
            <div className="flex align-items-center gap-3">
              <RadioButton
                inputId={`${id}-end-count`}
                name="endCondition"
                value="count"
                onChange={() => emit({ ...parsed, endType: "count" })}
                checked={endType === "count"}
              />
              <label htmlFor={`${id}-end-count`} className="flex align-items-center gap-2 text-900 cursor-pointer">
                After
                <InputNumber
                  value={count}
                  onValueChange={(e) => {
                    emit({
                      ...parsed,
                      count: e.value || 1,
                      endType: "count",
                    });
                  }}
                  onFocus={() => {
                    if (endType !== "count") {
                      emit({ ...parsed, endType: "count" });
                    }
                  }}
                  min={1}
                  max={maxCount}
                  inputClassName="w-4rem text-center"
                />
                occurrences
              </label>
            </div>

            <div className="flex align-items-center gap-3">
              <RadioButton
                inputId={`${id}-end-until`}
                name="endCondition"
                value="until"
                onChange={() =>
                  emit({
                    ...parsed,
                    endType: "until",
                    until: parsed.until ?? new Date(),
                  })
                }
                checked={endType === "until"}
              />
              <label htmlFor={`${id}-end-until`} className="text-900 cursor-pointer">
                On
              </label>
              <AdminCalendar
                value={until}
                onChange={(e) => {
                  emit({
                    ...parsed,
                    until: e.value as Date,
                    endType: "until",
                  });
                }}
                onFocus={() => {
                  if (endType !== "until") {
                    emit({ ...parsed, endType: "until" });
                  }
                }}
                placeholder="Select date"
                showIcon
                className="w-12rem"
              />
            </div>
          </div>
        </div>

        <div className="text-xs text-500 bg-gray-50 p-2 border-round">
          <strong>Rule Preview:</strong> {value}
        </div>
      </div>
    </FormField>
  );
}

